import { API_BASE_URL } from '../config/env';
import { ApiError, parseLaravelError } from './laravelError';
import { handleAuthFailure } from './authRealm';
import { adminSession, loyaltySession } from './session';

const JSON_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

export const FETCH_CREDENTIALS = 'include';

function buildUrl(path, params) {
  const base = API_BASE_URL.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  let url = `${base}${normalizedPath}`;

  if (params && Object.keys(params).length > 0) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        query.append(key, String(value));
      }
    });
    const qs = query.toString();
    if (qs) {
      url += `?${qs}`;
    }
  }

  return url;
}

function attachAuthHeaders(headers, auth) {
  if (auth === 'admin') {
    const token = adminSession.getBearerToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  } else if (auth === 'loyalty') {
    const token = loyaltySession.getBearerToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }
}

/**
 * @param {'GET'|'POST'|'PUT'|'PATCH'|'DELETE'} method
 * @param {string} path - Path relative to API_BASE_URL (e.g. '/promotions')
 * @param {{ body?: object, params?: object, auth?: 'admin'|'loyalty'|'none', context?: object }} [options]
 */
export async function laravelRequest(method, path, options = {}) {
  const { body, params, auth = 'none', context = {} } = options;

  const headers = { ...JSON_HEADERS };
  attachAuthHeaders(headers, auth);

  const config = {
    method,
    headers,
    credentials: FETCH_CREDENTIALS,
  };

  if (body !== undefined && body !== null && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(buildUrl(path, method === 'GET' ? params : undefined), config);

  if (response.ok) {
    if (response.status === 204) {
      return null;
    }
    return response.json().catch(() => null);
  }

  const errorPayload = await parseLaravelError(response);

  if (errorPayload.status === 401 && auth !== 'none') {
    handleAuthFailure(auth, {
      redirectOnFailure: context.redirectOnFailure !== false,
      ...context,
    });
    throw new ApiError({ ...errorPayload, silent: true });
  }

  throw new ApiError(errorPayload);
}

export const laravelGet = (path, options) => laravelRequest('GET', path, options);
export const laravelPost = (path, body, options) =>
  laravelRequest('POST', path, { ...options, body });
export const laravelPut = (path, body, options) =>
  laravelRequest('PUT', path, { ...options, body });
export const laravelDelete = (path, options) => laravelRequest('DELETE', path, options);
export const laravelPatch = (path, body, options) =>
  laravelRequest('PATCH', path, { ...options, body });
